import React, { useEffect, useState } from 'react';
import PortraitImage from './PortraitImage';
import { generateImageUrl } from '../../utils/imageUtils';
import { formatFriendlyDateTime, formatTimeUntil } from '../../utils/helpers';

const TIME_UNTIL_UPDATE_INTERVAL_MS = 15000;

export interface ScheduleEventRowProps {
  title?: string | undefined;
  location?: string | undefined;
  locationImageId?: string | undefined;
  startTimeEpochSeconds?: number | undefined;
  durationMinutes?: number | undefined;
  requiredCharacters?: string[] | undefined;
  requiredPortraits?: string[] | undefined;
  unconfirmedPortraits?: string[] | undefined;
  state?: string | undefined;
  instruction?: string | undefined;
  realWorldLocation?: string | undefined;
  className?: string | undefined;
}

const formatLocalTime = (startTimeEpochSeconds?: number): string => {
  const start = Number(startTimeEpochSeconds);
  if (!Number.isFinite(start) || start <= 0) {
    return 'TBD';
  }
  const formatted = formatFriendlyDateTime(start);
  return formatted || 'TBD';
};

const PORTRAIT_SIZE = 64; // thumbnail - 50% overlap onto image

const ScheduleEventRow: React.FC<ScheduleEventRowProps> = ({
  title,
  location,
  locationImageId,
  startTimeEpochSeconds,
  durationMinutes: _durationMinutes,
  requiredCharacters: _requiredCharacters,
  requiredPortraits,
  unconfirmedPortraits,
  state,
  instruction,
  realWorldLocation,
  className = '',
}) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!startTimeEpochSeconds) return;
    const id = setInterval(() => setTick((t) => t + 1), TIME_UNTIL_UPDATE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [startTimeEpochSeconds]);

  const titleText = title?.trim() || 'Untitled event';
  const timeText = formatLocalTime(startTimeEpochSeconds);
  const imageId = locationImageId?.trim() || 'generic_location';
  const locationImageUrl = generateImageUrl(imageId, 'location-image', 'card');
  const portraits = Array.isArray(requiredPortraits) ? requiredPortraits : [];
  const unconfirmedSet = new Set(Array.isArray(unconfirmedPortraits) ? unconfirmedPortraits : []);

  return (
    <article
      className={`schedule-event ${className}`.trim()}
      aria-label={`Event ${titleText}`}
      data-testid="schedule-event"
    >
      <div className="schedule-event-title">{titleText}</div>
      <div
        className="schedule-event-image-wrap"
        style={{
          position: 'relative',
          overflow: 'visible',
          paddingBottom: portraits.length > 0 ? PORTRAIT_SIZE / 2 : 0,
        }}
      >
        <img
          src={locationImageUrl}
          alt={location?.trim() || 'Event location'}
          className="schedule-event-image"
          data-testid="schedule-event-image"
        />
        {portraits.length > 0 && (
          <div
            className="schedule-event-portraits"
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 8,
              transform: 'translateY(-10%)',
              paddingLeft: 8,
              paddingRight: 8,
            }}
          >
            {portraits.map((portraitId, index) => {
              const resolvedId = portraitId || 'generic';
              const isUnconfirmed = unconfirmedSet.has(resolvedId);
              return (
                <div
                  key={`${portraitId}-${index}`}
                  className={isUnconfirmed ? 'schedule-event-portrait is-unconfirmed' : 'schedule-event-portrait'}
                  style={{
                    flexShrink: 0,
                    width: PORTRAIT_SIZE,
                    height: PORTRAIT_SIZE,
                  }}
                >
                  <PortraitImage
                    portraitId={resolvedId}
                    variant="thumbnail"
                    alt={`Required character ${index + 1}`}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
      {((state === 'scheduled' || state === 'proposed') &&
        startTimeEpochSeconds &&
        Number(startTimeEpochSeconds) > 0) && (
        <div className="schedule-event-meta-row">
          <span className="schedule-event-time" data-testid="schedule-event-time">
            {timeText}
          </span>
          {realWorldLocation?.trim() && (
            <span
              className="schedule-event-real-world-location"
              data-testid="schedule-event-real-world-location"
              style={{ fontStyle: 'italic' }}
            >
              {realWorldLocation.trim()}
            </span>
          )}
          {startTimeEpochSeconds && Number(startTimeEpochSeconds) > 0 && (
            <span className="schedule-event-time-until" data-testid="schedule-event-time-until">
              ({formatTimeUntil(startTimeEpochSeconds)})
            </span>
          )}
        </div>
      )}
      {instruction && (
        <div className="schedule-event-instruction" style={{ fontStyle: 'italic' }}>
          {instruction}
        </div>
      )}
    </article>
  );
};

export default ScheduleEventRow;
